import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Button, emitUnhandledError, logger } from '@/ui'
import { candidateWhatsAppResolveTemplateParametersQueryOptions } from '@jobilla/nexus-sdk'
import { useQueryClient } from '@tanstack/react-query'
import { getRouteApi, Link } from '@tanstack/react-router'
import { Loader2Icon, SendIcon, XIcon } from 'lucide-react'
import { motion } from 'motion/react'
import { useCallback, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { WhatsAppTemplate } from './types'

const companyRoute = getRouteApi('/_auth/company/$companyId')

export interface WhatsAppTemplateSheetProps {
	candidateId: string
	templates: WhatsAppTemplate[]
	onSendTemplate: (templateId: string, parameters: string[]) => Promise<void>
	onClose: () => void
}

export function WhatsAppTemplateSheet({ candidateId, templates, onSendTemplate, onClose }: WhatsAppTemplateSheetProps) {
	const queryClient = useQueryClient()
	const { t } = useTranslation('common')

	const [selectedTemplateId, setSelectedTemplateId] = useState('')
	const [parameters, setParameters] = useState<string[]>([])
	const [isResolvingParameters, setIsResolvingParameters] = useState(false)
	const [isSending, setIsSending] = useState(false)
	const latestTemplateRequestId = useRef(0)

	const selectedTemplate = useMemo(
		() => templates.find((template) => template.id === selectedTemplateId),
		[templates, selectedTemplateId],
	)

	const handleTemplateChange = useCallback(async (templateId: string) => {
		setSelectedTemplateId(templateId)

		const requestId = ++latestTemplateRequestId.current
		const template = templates.find((option) => option.id === templateId)

		if (!template) {
			setIsResolvingParameters(false)
			setParameters([])
			return
		}

		setParameters([])
		setIsResolvingParameters(true)

		try {
			const data = await queryClient.fetchQuery(
				candidateWhatsAppResolveTemplateParametersQueryOptions(candidateId, templateId),
			)

			if (requestId !== latestTemplateRequestId.current) {
				return
			}

			const resolved = Array.isArray(data.parameters) ? data.parameters : []
			setParameters(Array.from({ length: template.parameterCount }, (_, i) => resolved[i] || ''))
		} catch (err) {
			logger.error(err, {
				tags: { component: 'WhatsAppTemplateSheet', action: 'resolveParameters' },
			})

			if (requestId !== latestTemplateRequestId.current) {
				return
			}

			setParameters(Array.from({ length: template.parameterCount }, () => ''))
		} finally {
			if (requestId === latestTemplateRequestId.current) {
				setIsResolvingParameters(false)
			}
		}
	}, [templates, candidateId, queryClient])

	const handleParameterChange = useCallback((index: number, value: string) => {
		setParameters((prev) => {
			const next = [...prev]
			next[index] = value
			return next
		})
	}, [])

	const previewText = useMemo(() => {
		if (!selectedTemplate) {
			return ''
		}

		let text = selectedTemplate.bodyText
		parameters.forEach((param, index) => {
			text = text.replaceAll(`{{${index + 1}}}`, param || `[Parameter ${index + 1}]`)
		})
		return text
	}, [selectedTemplate, parameters])

	const handleSend = useCallback(async () => {
		if (!selectedTemplateId) {
			return
		}

		const hasEmptyParams = parameters.some((param) => !param.trim())
		if (hasEmptyParams && selectedTemplate?.parameterCount) {
			return
		}

		setIsSending(true)
		try {
			await onSendTemplate(selectedTemplateId, parameters)
		} catch (err) {
			emitUnhandledError({
				title: t('whatsapp.errorSendTitle'),
				description: err instanceof Error ? err.message : t('whatsapp.errorDefault'),
				cause: err,
			})
		} finally {
			setIsSending(false)
		}
	}, [selectedTemplateId, parameters, selectedTemplate, onSendTemplate, t])

	const canSend = Boolean(selectedTemplateId)
		&& !isResolvingParameters
		&& (!selectedTemplate?.parameterCount || parameters.every((param) => param.trim()))

	return (
		<motion.div
			initial={{ y: 12, opacity: 0 }}
			animate={{ y: 0, opacity: 1 }}
			exit={{ y: 12, opacity: 0 }}
			className="flex max-h-[60vh] flex-col rounded-t-xl border-t border-grey-100 bg-white shadow-[0_-4px_16px_rgba(0,0,0,0.06)]"
		>
			<div className="flex flex-shrink-0 items-center justify-between border-b border-grey-100 px-4 py-3">
				<h3 className="font-medium text-grey-900">{t('whatsapp.sendTemplateTitle')}</h3>
				<button
					type="button"
					onClick={onClose}
					aria-label={t('common.dismiss')}
					className="p-1 text-grey-400 transition-colors hover:text-grey-600"
				>
					<XIcon className="size-5" aria-hidden="true" />
				</button>
			</div>

			<div className="min-h-0 flex-1 space-y-4 overflow-y-auto p-4">
				<div>
					<label htmlFor="template-select" className="mb-1 block text-sm font-medium text-grey-700">
						{t('whatsapp.selectTemplate')}
					</label>
					<Select value={selectedTemplateId} onValueChange={handleTemplateChange}>
						<SelectTrigger id="template-select" className="w-full">
							<SelectValue placeholder={t('whatsapp.chooseTemplate')} />
						</SelectTrigger>
						<SelectContent>
							{templates.map((template) => {
								const isApproved = template.status === 'APPROVED'
								return (
									<SelectItem key={template.id} value={template.id} disabled={!isApproved}>
										{template.name} ({template.language})
										{!isApproved && ` - ${template.status.toLowerCase()}`}
									</SelectItem>
								)
							})}
						</SelectContent>
					</Select>
				</div>

				{selectedTemplate && selectedTemplate.parameterCount > 0 && (
					<div className="space-y-3">
						<label className="flex items-center gap-2 text-sm font-medium text-grey-700">
							{t('whatsapp.templateParameters')}
							{isResolvingParameters && (
								<span className="flex items-center gap-1 text-xs font-normal text-grey-500">
									<Loader2Icon className="size-3.5 animate-spin" />
									{t('whatsapp.resolvingParameters')}
								</span>
							)}
						</label>
						{Array.from({ length: selectedTemplate.parameterCount }, (_, index) => (
							<div key={index}>
								<label htmlFor={`param-${index}`} className="mb-1 block text-xs text-grey-500">
									{t('whatsapp.parameterLabel', { index: index + 1 })}
									{selectedTemplate.parameterExamples?.[index] && (
										<span className="ml-1 text-grey-400">
											{t('whatsapp.parameterExample', {
												example: selectedTemplate.parameterExamples[index],
											})}
										</span>
									)}
								</label>
								<input
									id={`param-${index}`}
									type="text"
									value={parameters[index] ?? ''}
									onChange={(e) => handleParameterChange(index, e.target.value)}
									disabled={isResolvingParameters}
									placeholder={t('whatsapp.parameterPlaceholder', {
										placeholder: `{{${index + 1}}}`,
									})}
									className="w-full rounded-lg border border-grey-200 px-3 py-2 text-sm focus:border-brand-600 focus:outline-none focus:ring-2 focus:ring-brand-600/20 disabled:bg-grey-50 disabled:text-grey-400"
								/>
							</div>
						))}
					</div>
				)}

				{selectedTemplate && (
					<div>
						<label className="mb-1 block text-sm font-medium text-grey-700">
							{t('whatsapp.messagePreview')}
						</label>
						<div className="whitespace-pre-wrap rounded-lg border border-grey-200 bg-grey-50 p-3 text-sm text-grey-700">
							{previewText || t('whatsapp.previewEmpty')}
						</div>
					</div>
				)}

				{templates.length === 0 && <NoTemplatesMessage />}
				{templates.length > 0 && templates.every((template) => template.status !== 'APPROVED') && (
					<div className="py-2 text-center text-sm text-amber-600">{t('whatsapp.allPending')}</div>
				)}
			</div>

			<div className="flex flex-shrink-0 items-center justify-end gap-2 border-t border-grey-100 px-4 py-3">
				<Button type="subtle" size="short" onClick={onClose}>
					{t('common.cancel')}
				</Button>
				<Button
					type="committal"
					size="short"
					onClick={() => void handleSend()}
					disabled={!canSend}
					isLoading={isSending}
				>
					<span className="flex items-center gap-1.5">
						<SendIcon className="size-4" />
						{t('common.send')}
					</span>
				</Button>
			</div>
		</motion.div>
	)
}

function NoTemplatesMessage() {
	const { t } = useTranslation('common')
	const { companyId } = companyRoute.useParams()

	return (
		<div className="py-4 text-center text-sm text-grey-500">
			{t('whatsapp.noTemplatesMessage')}{' '}
			<Link
				to="/company/$companyId/settings/whatsapp"
				params={{ companyId }}
				className="text-brand-600 underline hover:text-brand-700"
			>
				{t('whatsapp.goToWhatsAppSettings')}
			</Link>
		</div>
	)
}
